import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import { AuthProvider } from './context/AuthContext';
import { ProfileProvider } from './context/ProfileContext';
import { ThemeProvider } from './context/ThemeContext';
import { ToastProvider } from './components/shared/Toast';
import ProtectedRoute from './components/shared/ProtectedRoute';
import ErrorBoundary from './components/shared/ErrorBoundary';
import ConsentGate from './components/shared/ConsentGate';

// Home (the landing page) stays eager — it's the LCP and most-hit route. Every
// other route is code-split so the landing bundle doesn't ship the whole app
// (Interview pulls MediaPipe, Dashboard/Report pull charts, admin pages, etc.).
import Home from './pages/Home';
const Login = lazy(() => import('./pages/Login'));
const Signup = lazy(() => import('./pages/Signup'));
const TwoFactor = lazy(() => import('./pages/TwoFactor'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Profile = lazy(() => import('./pages/Profile'));
const Interview = lazy(() => import('./pages/Interview'));
const FeedbackReport = lazy(() => import('./components/feedback/FeedbackReport'));
const Counselor = lazy(() => import('./pages/Counselor'));
const ParentPortal = lazy(() => import('./pages/ParentPortal'));
const Settings = lazy(() => import('./pages/Settings'));
const Pricing = lazy(() => import('./pages/Pricing'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminUserDetail = lazy(() => import('./pages/AdminUserDetail'));
const SchoolDashboard = lazy(() => import('./pages/SchoolDashboard'));
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'));
const ReportGenerating = lazy(() => import('./components/interview/ReportGenerating'));
const Flashcard = lazy(() => import('./pages/Flashcard'));
const PostgradPrograms = lazy(() => import('./pages/PostgradPrograms'));
const PostgradProfile = lazy(() => import('./pages/PostgradProfile'));

// Minimal full-screen fallback while a route chunk loads.
function PageLoader() {
  return <div style={{ minHeight: '100vh', background: '#07090F' }} aria-hidden="true" />;
}

// Subtle 150ms fade between routes (CSS in index.css; disabled under
// prefers-reduced-motion). Keyed on pathname so it runs once per navigation.
function RouteFade({ children }) {
  const location = useLocation();
  return <div key={location.pathname} className="route-fade">{children}</div>;
}

function App() {
  // Keep Render backend alive — free tier sleeps after 15 min of inactivity
  useEffect(() => {
    const apiUrl = process.env.REACT_APP_API_URL || '';
    const ping = () => fetch(`${apiUrl}/health`).catch(() => {});
    ping();
    const interval = setInterval(ping, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <BrowserRouter>
      <ThemeProvider>
        <ToastProvider>
          <AuthProvider>
            <ProfileProvider>
              <ConsentGate />
              <ErrorBoundary>
              <RouteFade>
              <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public */}
                <Route path="/" element={<Home />} />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/2fa" element={<TwoFactor />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ForgotPassword />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/payment-success" element={<PaymentSuccess />} />
                {/* Postgrad programme browser — public, useful before signup */}
                <Route path="/postgrad/programmes" element={<PostgradPrograms />} />
                <Route path="/postgrad/programmes/:key" element={<PostgradPrograms />} />

                {/* Protected — all authenticated users */}
                <Route path="/dashboard" element={
                  <ProtectedRoute><Dashboard /></ProtectedRoute>
                } />
                <Route path="/profile" element={
                  <ProtectedRoute><Profile /></ProtectedRoute>
                } />
                <Route path="/interview/:id" element={
                  <ProtectedRoute><Interview /></ProtectedRoute>
                } />
                <Route path="/report/:id" element={
                  <ProtectedRoute><FeedbackReport /></ProtectedRoute>
                } />
                <Route path="/report-generating/:id" element={
                  <ProtectedRoute><ReportGenerating /></ProtectedRoute>
                } />
                <Route path="/flashcard/:id" element={
                  <ProtectedRoute><Flashcard /></ProtectedRoute>
                } />
                <Route path="/settings" element={
                  <ProtectedRoute><Settings /></ProtectedRoute>
                } />
                <Route path="/postgrad/profile" element={
                  <ProtectedRoute><PostgradProfile /></ProtectedRoute>
                } />

                {/* Role-specific */}
                <Route path="/counselor" element={
                  <ProtectedRoute requiredRole="counselor"><Counselor /></ProtectedRoute>
                } />
                <Route path="/school" element={
                  <ProtectedRoute requiredRole="counselor"><SchoolDashboard /></ProtectedRoute>
                } />
                <Route path="/parent" element={
                  <ProtectedRoute requiredRole="parent"><ParentPortal /></ProtectedRoute>
                } />

                {/* Admin — standalone, no auth context needed */}
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/admin/users/:id" element={<AdminUserDetail />} />

                {/* Fallback */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              </Suspense>
              </RouteFade>
              </ErrorBoundary>
            </ProfileProvider>
          </AuthProvider>
        </ToastProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;